import { Link } from "react-router-dom";
import { CalendarHeart, Brain, Waves, Scale, Compass, Sprout, Wallet } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n/LanguageContext";

const OFFERING_ICONS = {
  "adhd-coaching": Brain,
  "stress-management": Waves,
  "work-life-balance": Scale,
  "career-transitions": Compass,
  "personal-growth": Sprout,
};

export default function Services() {
  const { t, to } = useLang();
  const s = t.services;

  return (
    <>
      <section data-testid="services-header" className="pt-32 sm:pt-40 pb-14 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{s.kicker}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] max-w-3xl text-balance">
            {s.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-clay leading-relaxed max-w-2xl">{s.intro}</p>
          <Link
            to={to("/booking")}
            data-testid="services-header-book-btn"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
          >
            <CalendarHeart className="w-5 h-5" />
            {s.bookCta}
          </Link>
        </FadeUp>
      </section>

      <section data-testid="offerings-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-14">
        <FadeUp className="max-w-2xl mb-10">
          <p data-testid="offerings-intro" className="text-base sm:text-lg text-clay leading-relaxed">{s.offeringsIntro}</p>
        </FadeUp>
        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {s.offerings.map((o) => {
            const Icon = OFFERING_ICONS[o.id];
            return (
              <StaggerItem key={o.id} className={o.flagship ? "md:col-span-2" : ""}>
                <div
                  data-testid={`offering-${o.id}`}
                  className={`h-full rounded-3xl p-8 sm:p-10 transition-transform duration-300 hover:-translate-y-1 ${
                    o.flagship
                      ? "bg-espresso text-linen shadow-[0_30px_70px_-30px_rgba(44,37,32,0.6)]"
                      : "bg-white border border-border shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)]"
                  }`}
                >
                  <div className="flex items-start gap-5">
                    <span
                      className={`w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center ${
                        o.flagship ? "bg-terracotta/25" : "bg-terracotta-soft"
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${o.flagship ? "text-[#E0A98F]" : "text-terracotta"}`} />
                    </span>
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl font-semibold">{o.title}</h3>
                      {o.flagship && (
                        <span className="mt-2.5 inline-flex text-[10px] uppercase tracking-[0.2em] font-sans font-bold bg-terracotta text-linen rounded-full px-3 py-1">
                          {s.flagshipBadge}
                        </span>
                      )}
                      <p className={`mt-3 text-sm sm:text-base leading-relaxed ${o.flagship ? "text-linen/80" : "text-clay"}`}>
                        {o.description}
                      </p>
                      <div className={`mt-5 pt-5 border-t ${o.flagship ? "border-white/15" : "border-border"}`}>
                        <p className={`text-xs uppercase tracking-[0.18em] font-semibold mb-1.5 ${o.flagship ? "text-[#E0A98F]" : "text-sage-dark"}`}>
                          {s.efLabel}
                        </p>
                        <p className={`text-sm leading-relaxed font-medium ${o.flagship ? "text-linen" : "text-espresso"}`}>{o.helps}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      <section data-testid="services-payment-link-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
        <FadeUp className="text-center max-w-2xl mx-auto">
          <p className="text-base sm:text-lg text-clay leading-relaxed">{s.paySectionText}</p>
          <Link
            to={to("/payments")}
            data-testid="services-how-payment-works-btn"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
          >
            <Wallet className="w-5 h-5" />
            {s.payLinkLabel}
          </Link>
          <p className="mt-8 text-sm text-taupe">
            {s.notSurePre}{" "}
            <Link to={to("/booking")} data-testid="pricing-discovery-link" className="font-semibold text-terracotta hover:text-terracotta-dark">
              {s.notSureLink}
            </Link>{" "}
            {s.notSurePost}
          </p>
        </FadeUp>
      </section>

      <section data-testid="services-faq-section" className="max-w-3xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <FadeUp className="mb-10 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{s.faqKicker}</p>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight">{s.faqTitle}</h2>
        </FadeUp>
        <FadeUp>
          <Accordion type="single" collapsible className="space-y-3" data-testid="services-faq-accordion">
            {s.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-2xl bg-white border border-border px-6">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-lg font-semibold py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-content-${i}`} className="text-sm sm:text-base text-clay leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </section>
    </>
  );
}
